import { FundCard } from "@/components/fund/FundCard";
import { EtfEyebrow } from "@/components/layout/EtfEyebrow";
import { EtfNewsletter } from "@/components/layout/EtfNewsletter";
import { Button } from "@/components/ui/button";
import type { Fund } from "@/data/funds";
import { useFundList } from "@/hooks/use-fund-list";
import { formatAsOfMdy, formatAum, fundLiveAumDollars } from "@/hooks/use-fund-live-data";
import { BLOG_BASE_URL } from "@/lib/blog-host";
import { BRAND } from "@/lib/brand";
import { Seo } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

const FUND_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "thematic", label: "Thematic" },
  { key: "leveraged", label: "2x Leveraged" },
  { key: "buffer", label: "Buffer" },
  { key: "fixed", label: "Fixed Income" },
] as const;

type FundCategoryKey = (typeof FUND_CATEGORIES)[number]["key"];

const tickerStatsMeta = [
  { label: "Total AUM", sublabel: "Live · Sum of public funds", tooltip: "", primary: true },
  { label: "ETFs", tooltip: "" },
  { label: "Founded", tooltip: "" },
  {
    label: "Avg. Thematic ETF Net Expense Ratio",
    tooltip:
      "Average net expense ratio across all Thematic ETFs in our fund lineup, weighted equally.",
  },
  {
    label: "Avg. Buffer ETF Net Expense Ratio",
    tooltip:
      "Average net expense ratio across all Buffer ETFs in our fund lineup, weighted equally.",
  },
] as const;

/** Parse "0.49%" / "1.08%" / "X" into a number; return null when unparseable. */
function parsePercent(value: string | undefined): number | null {
  if (!value) return null;
  const m = value.match(/(-?\d+(?:\.\d+)?)/);
  if (!m) return null;
  const n = Number.parseFloat(m[1] ?? "");
  return Number.isFinite(n) ? n : null;
}

/** Equally-weighted average of a fund-type's net expense ratio across the
 *  live fund lineup. Returns "0.30%" / "0.49%" / "—" if no fund of that type. */
function avgExpenseRatioForType(funds: readonly Fund[], type: string): string {
  const values: number[] = [];
  for (const f of funds) {
    // DDB uses `fundType` (not `type`).
    if (f.fundType !== type) continue;
    const n = parsePercent(f.expenseRatio);
    if (n != null) values.push(n);
  }
  if (values.length === 0) return "—";
  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  return `${avg.toFixed(2)}%`;
}

const philosophyColumns = [
  {
    label: "Innovation First",
    title: "A New Breed of ETFs",
    desc: "We push the boundaries of what's possible in the ETF landscape, designing products built for tomorrow's markets, not yesterday's playbook.",
  },
  {
    label: "Accessibility",
    title: "Built for a Broad Audience",
    desc: "Corgi ETFs are engineered to serve a wide spectrum of investors, bringing sophisticated strategies within reach.",
  },
  {
    label: "Conviction",
    title: "Always Alert",
    desc: "Built for the future of investing, our strategies stay adaptive, disciplined, and ready for what markets demand next.",
  },
] as const;

// Number of fund cards visible by default before "Show more" is clicked.
// Static reference shows 2 rows × 4 columns on first paint at desktop widths.
const FUND_GRID_INITIAL_LIMIT = 8;

// When the "All" tab is active, the FUNDS array order surfaces ~30 thematic
// cards before any buffer card, so an 8-card preview hides the entire buffer
// family behind "Show More". User direction: buffer cards must be visible
// without clicking "Show More" on the All tab. We append every buffer card
// to the initial preview slice so the All tab always renders thematic + the
// full buffer family, without changing the limit for other filters.
function previewFundsForAll<T extends { fundType: string; id: string }>(
  funds: readonly T[],
  limit: number,
): T[] {
  const head = funds.slice(0, limit);
  const headIds = new Set(head.map((f) => f.id));
  const buffersInTail = funds
    .slice(limit)
    .filter((f) => f.fundType === "buffer" && !headIds.has(f.id));
  return [...head, ...buffersInTail];
}

export function HomePage() {
  const [activeFilter, setActiveFilter] = useState<FundCategoryKey>("all");
  // Grid opens fully expanded so every fund is visible on first paint
  // (mirrors main's UX from PR #2011). The toggle still exists so the
  // user can collapse back to the preview if they want.
  const [fundGridExpanded, setFundGridExpanded] = useState(true);

  // Live fund list from DDB (1 h TTL via Valkey). Falls back to empty array
  // while loading — the grid renders nothing, the ticker tape is silent, and
  // counts show 0. There is no skeleton: the section headers are still
  // visible, so the page doesn't feel broken on the first paint.
  const { data: allFunds = [] } = useFundList();

  // Featured funds gate — funds with `featuredOnHomepage === true` are surfaced
  // first in the grid and in the ETF ticker tape. Falls back to all funds if
  // no fund has the flag set (graceful degradation while DDB records are sparse).
  const featuredFunds = allFunds.filter((f) => f.featuredOnHomepage === true);
  const displayFunds = featuredFunds.length > 0 ? featuredFunds : allFunds;

  // Fund grid shows featured funds only (with graceful fallback to all funds
  // when no fund has featuredOnHomepage=true). Tabs, stats, and the ticker
  // tape use the full lineup so totals stay accurate.
  // DDB uses `fundType` (not `type`).
  const filteredFundsRaw =
    activeFilter === "all" ? displayFunds : displayFunds.filter((f) => f.fundType === activeFilter);

  // Tab counts reflect the full lineup (not just featured) so the filter badges
  // show accurate totals even while the grid is filtered to featured funds.
  const dynamicCounts: Record<FundCategoryKey, number> = {
    all: allFunds.length,
    thematic: allFunds.filter((f) => f.fundType === "thematic").length,
    leveraged: allFunds.filter((f) => f.fundType === "leveraged").length,
    buffer: allFunds.filter((f) => f.fundType === "buffer").length,
    fixed: allFunds.filter((f) => f.fundType === "fixed").length,
  };

  // Order cards by AUM descending so the largest funds surface first. AUM is the
  // server-computed live value (dollars); funds without a holdings snapshot sort
  // to the bottom. The tab-filtered order is a stable tiebreaker.
  const filteredFunds = useMemo(() => {
    return [...filteredFundsRaw].sort(
      (a, b) => (fundLiveAumDollars(b) ?? 0) - (fundLiveAumDollars(a) ?? 0),
    );
  }, [filteredFundsRaw]);

  // Stats strip + ticker tape always reflect the full fund lineup. Total AUM
  // sums the server-computed live AUM (SUM of holdings.market_value) in dollars
  // — no DDB.aum, no string parsing. The "as of" label uses the most-recent
  // NAV-file date (daily_nav rate_date, ISO sorts correctly) so it matches the
  // fund pages' NAV / price / AUM "as of" — NOT the holdings position_date.
  const totalAum = allFunds.reduce((sum, f) => sum + (fundLiveAumDollars(f) ?? 0), 0);
  const latestLiveAsOf = allFunds
    .map((f) => f.latestNavAsOfDate)
    .filter((d): d is string => Boolean(d))
    .sort()
    .pop();
  const latestAsOf = formatAsOfMdy(latestLiveAsOf);
  const aumSubLabel = latestAsOf ? `(as of ${latestAsOf})` : "(loading…)";

  // ETF ticker tape — real prices from the daily_nav overlay on the funds-list
  // response (latestMarketPrice, falling back to latestNav). Funds with no
  // ingested price row are dropped (no "—" placeholders). Sorted by live AUM so
  // the largest funds lead, mirroring Dane's prod ticker. Red/green follows the
  // 1-day market-price change %; absent change data colours positive (green).
  const etfScrollItems = useMemo(() => {
    const priced = allFunds
      .map((fund) => {
        const raw = fund.latestMarketPrice ?? fund.latestNav;
        const priceNum = raw != null ? Number(raw) : Number.NaN;
        if (!Number.isFinite(priceNum) || priceNum <= 0) return null;
        const changePct =
          fund.latestMarketPriceChangePct != null ? Number(fund.latestMarketPriceChangePct) : null;
        const dir: "pos" | "neg" =
          changePct != null && Number.isFinite(changePct) && changePct < 0 ? "neg" : "pos";
        // DDB uses `id` for the URL slug (not `slug`).
        return {
          sym: fund.ticker,
          price: `$${priceNum.toFixed(2)}`,
          dir,
          path: `/${fund.id}`,
          aum: fundLiveAumDollars(fund) ?? 0,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x != null)
      .sort((a, b) => b.aum - a.aum)
      .map((x) => ({ sym: x.sym, price: x.price, dir: x.dir, path: x.path }));

    // Graceful fallback: if no fund has an ingested price yet (fresh env), show
    // every ticker symbol-only rather than an empty banner — never an em-dash.
    if (priced.length === 0) {
      return allFunds.map((fund) => ({
        sym: fund.ticker,
        price: "",
        dir: "pos" as "pos" | "neg",
        path: `/${fund.id}`,
      }));
    }
    return priced;
  }, [allFunds]);

  // Repeat the lineup enough times that one half of the track (the -50% scroll
  // distance) always exceeds the widest viewport, so the loop is seamless with
  // no gap. One copy of the live lineup already dwarfs any screen, so 2 copies
  // suffice for dev/prod fund counts; the math stays correct (and even) if the
  // lineup ever shrinks. The count must be even so -50% lands on an identical copy.
  const ITEM_PX = 138; // measured ticker cell width (symbol + price + px-7 padding)
  const MIN_HALF_PX = 4000; // ≥ ultrawide viewport so a half never shows a gap
  const tickerItemCount = etfScrollItems.length || 1;
  const copiesPerHalf = Math.max(1, Math.ceil(MIN_HALF_PX / (tickerItemCount * ITEM_PX)));
  const etfScrollRepeat = copiesPerHalf * 2;
  // Scroll at a constant ~92 px/s to match Dane prod — the prior ~1.1 s/item
  // rate ran ~250 px/s (2.7× faster) and read as a blur. Derive the loop length
  // from the rendered track width (repeat × items × item px) ÷ target px/s; the
  // keyframe translates -50%, so this is the seen scroll rate. Clamp to a sane
  // 180–400 s so degenerate tiny/huge lineups still feel reasonable.
  const TARGET_PX_PER_SEC = 92;
  const trackWidthPx = etfScrollRepeat * tickerItemCount * ITEM_PX;
  const etfScrollDurationSec = Math.min(
    400,
    Math.max(180, Math.round(trackWidthPx / TARGET_PX_PER_SEC)),
  );

  const tickerStats = [
    {
      ...tickerStatsMeta[0],
      value: totalAum > 0 ? formatAum(totalAum) : "—",
      sublabel: aumSubLabel,
    },
    { ...tickerStatsMeta[1], value: String(dynamicCounts.all) },
    { ...tickerStatsMeta[2], value: String(BRAND.foundedYear) },
    { ...tickerStatsMeta[3], value: avgExpenseRatioForType(allFunds, "thematic") },
    { ...tickerStatsMeta[4], value: avgExpenseRatioForType(allFunds, "buffer") },
  ];

  return (
    <>
      <Seo
        canonicalPath="/"
        description="Corgi Funds builds thematic, 2x leveraged, structured buffer, and fixed-income ETFs — direct access to disruptive themes and companies shaping the future."
        keywords="ETFs, thematic ETFs, leveraged ETFs, buffer ETFs, fixed income ETFs, Corgi Funds"
      />
      {/* Hero */}
      <section className="etf-landing-hero relative flex flex-col justify-center px-10 pt-12 pb-10 border-b border-[var(--etf-border)] max-w-[1200px] mx-auto h-[calc(100dvh-57px-45px)] max-md:px-5 max-md:py-8 max-md:h-auto max-md:min-h-[calc(100dvh-56px-44px)] max-[480px]:px-4 max-[480px]:py-7">
        {/* Eyebrow */}
        <EtfEyebrow className="relative z-[1]">Corgi Funds</EtfEyebrow>

        {/* Title */}
        <h1 className="relative z-[1] font-[family-name:var(--font-f37bolton)] text-[clamp(40px,5.5vw,82px)] font-normal leading-[1.05] tracking-[-0.02em] mb-7 max-md:text-[clamp(32px,9vw,56px)] max-md:mb-6 max-[480px]:text-[clamp(28px,10vw,42px)]">
          <em className="not-italic text-[var(--etf-orange)]">Corgi's</em> ETFs are designed to
          offer direct access to disruptive themes and companies shaping the future.
        </h1>

        {/* Bottom row: description + CTAs */}
        <div className="relative z-[1] flex items-end justify-between gap-15 flex-wrap max-md:flex-col max-md:items-start max-md:gap-7 max-[480px]:gap-6">
          <p className="text-[17px] italic font-light text-[var(--etf-white2)] leading-[1.75] max-w-[480px] max-md:text-[15px]">
            From frontier technology to leveraged strategies — we build ETFs for what we believe
            comes next.
          </p>

          <div className="flex gap-3 shrink-0 max-md:w-full">
            <Link to="/products" className="max-md:w-full">
              <Button className="rounded-none bg-[var(--etf-orange)] text-[var(--etf-white)] px-7 py-3.5 font-[family-name:var(--font-instrument)] text-base font-medium tracking-widest uppercase shadow-[0_4px_20px_rgba(255,85,0,0.2)] hover:opacity-[0.88] hover:bg-[var(--etf-orange)] active:translate-y-0 active:shadow-[0_4px_20px_rgba(255,85,0,0.2)] max-md:w-full max-md:text-sm max-md:px-5 max-md:py-3">
                View All Products →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ETF Scroll Banner */}
      <div className="etf-scroll-banner overflow-hidden bg-[var(--etf-black2)] border-b border-[var(--etf-border)] h-[44px] flex items-center relative">
        {/* Duration + repeat count are computed above for a constant, readable
            per-item scroll speed clamped to a sane loop length. The CSS keeps
            `animation-iteration-count: infinite`; this inline style overrides
            only the duration, so the tape never stops. */}
        <div
          className="etf-scroll-track flex items-center w-max"
          style={{ animationDuration: `${etfScrollDurationSec}s` }}
        >
          {/* Repeat the lineup enough times (even count) that even ultrawide
              viewports don't see a gap and the -50% scroll keyframe lands on an
              identical copy, looping seamlessly. */}
          {Array.from({ length: etfScrollRepeat }, () => etfScrollItems)
            .flat()
            .map((item, i) => ({ item, uid: `${i}-${item.sym}` }))
            .map(({ item, uid }) => {
              const cls =
                "font-[family-name:var(--font-instrument)] flex items-center gap-2.5 px-7 border-r border-[var(--etf-border2)] whitespace-nowrap h-[44px] text-xs font-medium";
              const priceCls = cn(
                item.dir === "neg" ? "text-[var(--etf-red)]" : "text-[var(--etf-green)]",
              );
              return item.path ? (
                <Link
                  key={uid}
                  to={item.path}
                  className={cn(
                    cls,
                    "no-underline text-inherit transition-colors duration-150 hover:bg-[rgba(255,85,0,0.06)]",
                  )}
                >
                  <span className="text-[var(--etf-white)] tracking-[0.08em]">{item.sym}</span>
                  {item.price ? <span className={priceCls}>{item.price}</span> : null}
                </Link>
              ) : (
                <div key={uid} className={cls}>
                  <span className="text-[var(--etf-white)] tracking-[0.08em]">{item.sym}</span>
                  {item.price ? <span className={priceCls}>{item.price}</span> : null}
                </div>
              );
            })}
        </div>
        <Link
          to="/products"
          className="absolute right-0 top-0 bottom-0 px-6 pl-12 bg-[linear-gradient(to_right,transparent,var(--etf-black2)_35%)] flex items-center font-[family-name:var(--font-instrument)] text-[11px] font-medium tracking-[0.12em] text-[var(--etf-white2)] no-underline uppercase z-10 transition-colors duration-150 hover:text-[var(--etf-white)]"
        >
          View All →
        </Link>
      </div>

      {/* Ticker Strip */}
      <div className="etf-ticker-strip flex border-t border-b border-[var(--etf-border)] bg-[var(--etf-black)] relative overflow-visible max-md:overflow-x-auto max-md:[-webkit-overflow-scrolling:touch] max-md:flex-nowrap [&::-webkit-scrollbar]:max-md:hidden">
        {tickerStats.map((stat, i) => (
          <div
            key={stat.label}
            className={cn(
              "etf-ticker-cell px-[22px] pt-2.5 pb-3 border-r border-[var(--etf-border)] flex-1 min-w-[140px] bg-transparent text-left flex flex-col justify-between gap-1.5 relative transition-[background] duration-300 ease-in-out animate-[tickerFadeIn_0.5s_ease_both] hover:bg-[rgba(255,85,0,0.04)] last:border-r-0 max-md:flex-shrink-0 max-md:min-w-[160px] max-md:px-[18px] max-md:py-4",
              "primary" in stat &&
                stat.primary &&
                "before:content-[''] before:absolute before:top-[-1px] before:left-0 before:right-0 before:h-[2px] before:bg-[var(--etf-orange)]",
            )}
            style={{ animationDelay: `${100 + i * 80}ms` }}
          >
            <div
              className={cn(
                "font-[family-name:var(--font-instrument)] text-[9px] tracking-[0.14em] uppercase text-[var(--etf-white3)] leading-[1.35] block",
                stat.tooltip && "group/tooltip relative cursor-pointer",
              )}
            >
              {stat.label}
              {"sublabel" in stat && stat.sublabel && (
                <span className="block text-[0.78em] opacity-55 font-normal mt-[2px] tracking-[0.08em]">
                  {stat.sublabel}
                </span>
              )}
              {stat.tooltip && (
                <span className="absolute top-[calc(100%+10px)] left-1/2 -translate-x-1/2 w-[250px] bg-[var(--etf-black2)] text-[var(--etf-white)] border border-[var(--etf-border)] rounded-md px-3.5 py-3 shadow-[0_4px_12px_rgba(0,0,0,0.5)] font-[family-name:var(--font-geist)] text-[13px] font-normal leading-normal normal-case tracking-[0.02em] text-left whitespace-normal z-[100] pointer-events-none opacity-0 invisible transition-[opacity,visibility] duration-200 group-hover/tooltip:opacity-100 group-hover/tooltip:visible before:content-[''] before:absolute before:bottom-full before:left-1/2 before:border-solid before:border-transparent before:border-b-[var(--etf-border)] before:border-[7px] before:-ml-[7px] before:pointer-events-none after:content-[''] after:absolute after:bottom-full after:left-1/2 after:border-solid after:border-transparent after:border-b-[var(--etf-black2)] after:border-[6px] after:-ml-[6px] after:pointer-events-none">
                  <span style={{ color: "var(--etf-orange)" }}>{stat.tooltip}</span>
                </span>
              )}
            </div>
            <div
              className={cn(
                "font-[family-name:'Geist_Mono','SF_Mono',ui-monospace,monospace] text-[clamp(16px,1.3vw,20px)] font-medium text-[var(--etf-white)] leading-none [font-variant-numeric:tabular-nums] tracking-[-0.03em] max-md:text-[20px] max-[480px]:text-[18px]",
                "primary" in stat && stat.primary && "text-[var(--etf-orange)]",
              )}
            >
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* Mission / Our Approach */}
      <section className="grid grid-cols-1 border-b border-[var(--etf-border)] relative overflow-hidden">
        {/* Background image */}
        <img
          src="/images/etf/approach-bg.webp"
          alt=""
          loading="lazy"
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-screen h-full object-cover opacity-20 z-0"
        />
        <div className="py-[72px] px-12 max-md:py-12 max-md:px-6 relative z-[1]">
          <EtfEyebrow>Our Approach</EtfEyebrow>
          <div className="font-[family-name:var(--font-f37bolton)] text-[clamp(22px,2.75vw,34px)] leading-[1.08] tracking-[-0.02em] text-[var(--etf-white)] mb-5 max-w-[38ch]">
            A Friendly Approach to Market Access. Our ETFs are designed with evolving market
            conditions in mind.
          </div>
          <p className="text-sm italic font-light text-[var(--etf-white2)] leading-[1.65] mb-[22px] max-w-[38ch]">
            Our funds emphasize transparency, efficiency, and thoughtful construction, offering
            investors access to modern strategies through the simplicity and liquidity of an ETF
            wrapper.
          </p>
          <a
            href={`${BLOG_BASE_URL}/`}
            className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-orange)] no-underline tracking-[0.06em] inline-flex items-center gap-1.5 transition-[gap] duration-200 hover:gap-2.5"
          >
            Read Our Thoughts →
          </a>
        </div>

        {/* CostShowcase removed */}
      </section>

      {/* Products Section */}
      <section className="border-b border-[var(--etf-border)]">
        <div className="flex items-center justify-between px-10 py-6 border-b border-[var(--etf-border)] max-md:px-5 max-md:py-5 max-md:gap-3">
          <div className="font-[family-name:var(--font-f37bolton)] text-2xl text-[var(--etf-white)] flex items-center gap-3 tracking-[-0.02em] leading-none before:content-[''] before:inline-block before:w-[3px] before:h-5 before:bg-[var(--etf-orange)] max-md:text-xl">
            Our ETF Suite
          </div>
          <Link
            to="/products"
            className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-orange)] no-underline tracking-[0.06em] inline-flex items-center gap-1.5 transition-[gap] duration-200 hover:gap-2.5 max-md:text-xs max-md:whitespace-nowrap"
          >
            View All {dynamicCounts.all} Funds →
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex px-10 border-b border-[var(--etf-border)] bg-[var(--etf-black2)] max-md:px-5 max-md:overflow-x-auto max-md:[-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:max-md:hidden [scrollbar-width:none]">
          {FUND_CATEGORIES.map((tab) => {
            const isActive = activeFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                aria-pressed={isActive}
                className={cn(
                  "font-[family-name:var(--font-instrument)] text-sm py-4 px-6 bg-transparent border-none border-b-2 border-b-transparent cursor-pointer transition-all duration-150 flex items-center gap-2 tracking-[0.06em] whitespace-nowrap first:pl-4",
                  isActive
                    ? "text-[var(--etf-white)] border-b-[var(--etf-orange)]"
                    : "text-[var(--etf-white3)] hover:text-[var(--etf-white2)]",
                )}
                onClick={() => {
                  setActiveFilter(tab.key);
                  setFundGridExpanded(true);
                }}
              >
                {tab.label}
                <span
                  className={cn(
                    "text-[9px] py-[2px] px-1.5 rounded-[10px]",
                    isActive
                      ? "bg-[rgba(255,85,0,0.15)] text-[var(--etf-orange)]"
                      : "bg-[rgba(255,255,255,0.08)] text-[var(--etf-white3)]",
                  )}
                >
                  {dynamicCounts[tab.key]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Fund Grid — `isolation: isolate` creates a fresh stacking context
            so the per-card hover z-jump (z-1 → z-50) and inner expense-ratio
            tooltip (z-100) lift above neighboring cards without leaking under
            the page header / sticky nav. Mirrors static-reference index.html
            commit 4ca35f4 `.fund-grid { isolation: isolate; }`. */}
        <div className="grid grid-cols-4 max-lg:grid-cols-2 max-md:grid-cols-1 gap-px bg-[var(--etf-border)] isolate">
          {(() => {
            if (fundGridExpanded) return filteredFunds;
            if (activeFilter === "all") {
              return previewFundsForAll(filteredFunds, FUND_GRID_INITIAL_LIMIT);
            }
            return filteredFunds.slice(0, FUND_GRID_INITIAL_LIMIT);
          })().map((fund) => (
            <FundCard key={fund.id} fund={fund} />
          ))}
        </div>
        {/* "Show more" reveals the remaining cards in-place when the active
            filter has more funds than the initial limit. The All tab already
            surfaces buffer cards inline (see `previewFundsForAll` above), so
            the count compares against the buffer-included preview to avoid
            an off-by-one on the "X more" label. Switching tabs resets
            `fundGridExpanded` so the user lands back at the preview for each
            filter. */}
        {!fundGridExpanded &&
          (() => {
            const previewLength =
              activeFilter === "all"
                ? previewFundsForAll(filteredFunds, FUND_GRID_INITIAL_LIMIT).length
                : Math.min(filteredFunds.length, FUND_GRID_INITIAL_LIMIT);
            return filteredFunds.length > previewLength;
          })() && (
            <div className="p-5 px-10 text-center border-t border-[var(--etf-border)] max-md:px-5">
              <button
                type="button"
                onClick={() => setFundGridExpanded(true)}
                className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-orange)] bg-transparent border-none cursor-pointer no-underline tracking-[0.06em] inline-flex items-center gap-1.5 transition-[gap] duration-200 hover:gap-2.5"
              >
                Show More (
                {filteredFunds.length -
                  (activeFilter === "all"
                    ? previewFundsForAll(filteredFunds, FUND_GRID_INITIAL_LIMIT).length
                    : Math.min(filteredFunds.length, FUND_GRID_INITIAL_LIMIT))}{" "}
                more) →
              </button>
            </div>
          )}
        <div className="p-5 px-10 text-center border-t border-[var(--etf-border)]">
          <Link
            to="/products"
            className="font-[family-name:var(--font-instrument)] text-[14px] leading-[1.5] text-[var(--etf-orange)] no-underline tracking-[0.06em] inline-flex items-center gap-1.5 transition-[gap] duration-200 hover:gap-2.5"
          >
            View All Funds →
          </Link>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="relative overflow-hidden border-b border-[var(--etf-border)]">
        {/* Background image */}
        <img
          src="/images/etf/philosophy-bg.webp"
          alt=""
          loading="lazy"
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-screen h-full object-cover opacity-20 z-0"
        />
        <div className="py-20 px-10 max-w-[1200px] mx-auto max-md:py-12 max-md:px-5">
          <div className="relative z-[1] flex items-end justify-between mb-[52px] flex-wrap gap-10 max-md:flex-col max-md:items-start max-md:gap-6 max-md:mb-8">
            <div>
              <EtfEyebrow>Investment Philosophy</EtfEyebrow>
              <div className="font-[family-name:var(--font-f37bolton)] text-[clamp(36px,4vw,56px)] font-normal leading-none text-[var(--etf-white)] max-w-[840px] tracking-[-0.02em]">
                Access should not be limited by size or status. Corgi delivers sophisticated,
                targeted investment strategies built for a{" "}
                <em className="not-italic text-[var(--etf-orange)]">broader audience.</em>
              </div>
            </div>
            <a
              href={`${BLOG_BASE_URL}/`}
              className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-orange)] no-underline tracking-[0.06em] inline-flex items-center gap-1.5 transition-[gap] duration-200 hover:gap-2.5"
            >
              Learn More →
            </a>
          </div>

          <div className="relative z-[1] grid grid-cols-3 max-md:grid-cols-1 gap-px bg-[var(--etf-border)] border border-[var(--etf-border)]">
            {philosophyColumns.map((col) => (
              <div
                key={col.label}
                className="bg-[var(--etf-black)] py-9 px-8 max-md:py-7 max-md:px-6"
              >
                <div className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.14em] uppercase text-[var(--etf-orange)] mb-4 flex items-center gap-2.5 after:content-[''] after:flex-1 after:h-px after:bg-[var(--etf-border)]">
                  {col.label}
                </div>
                <div className="font-[family-name:var(--font-f37bolton)] text-[22px] tracking-[-0.02em] text-[var(--etf-white)] mb-3 leading-none">
                  {col.title}
                </div>
                <div className="text-sm italic font-light leading-[1.7] text-[var(--etf-white2)]">
                  {col.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Insights - removed from homepage */}

      {/* Newsletter */}
      <EtfNewsletter />
    </>
  );
}
